import { makeAutoObservable } from 'mobx';
import { timeoutMessage } from '../utils/timeoutMessage';

/**
 * Validasyon verilerini global olarak tutan store
 * Login'de bir kere yüklenir, tüm uygulamada kullanılır
 */
class ValidationStore {
  constructor() {
    this.modelValidations = {}; // Tüm modellerin validasyon kuralları
    this.uiValidationRules = {}; // UI için hazır kurallar
    this.isLoaded = false; // Yüklenme durumu
    this.loading = false; // Yükleniyor durumu
    this.error = null; // Hata durumu

    makeAutoObservable(this);
  }

  /**
   * Login'de validasyon verilerini yükler
   */
  async loadValidationData() {
    if (this.isLoaded || this.loading) return;

    this.loading = true;
    this.error = null;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_SERVER}api/validation/models`);
      if (response.status === 504) throw new Error(timeoutMessage());
      const data = await response.json();
      
      this.modelValidations = data;
      this.isLoaded = true;
      
      console.log('✅ Validasyon verileri yüklendi:', Object.keys(data).length, 'model');
    } catch (error) {
      console.error('❌ Validasyon verileri yüklenemedi:', error);
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Belirli bir model için validasyon kurallarını döndürür
   */
  getModelValidation(modelName) {
    return this.modelValidations[modelName] || {};
  }

  /**
   * Belirli bir field için validasyon kuralı döndürür
   */
  getFieldValidation(modelName, fieldName) {
    const model = this.getModelValidation(modelName);
    return model[fieldName] || null;
  }

  /**
   * Field için maksimum uzunluk döndürür
   */
  getMaxLength(modelName, fieldName) {
    const field = this.getFieldValidation(modelName, fieldName);
    return field?.maxLength || null;
  }

  /**
   * Field zorunlu mu kontrolü
   */
  isRequired(modelName, fieldName) {
    const field = this.getFieldValidation(modelName, fieldName);
    return field?.required || false;
  }

  /**
   * String alan mı kontrolü
   */
  isStringField(modelName, fieldName) {
    const field = this.getFieldValidation(modelName, fieldName);
    return field?.maxLength !== undefined;
  }

  /**
   * Input için validasyon props'ları döndürür
   */
  getInputValidationProps(modelName, fieldName) {
    const field = this.getFieldValidation(modelName, fieldName);
    
    if (!field || !field.maxLength) {
      return {};
    }

    return {
      maxLength: field.maxLength,
      showClearButton: !field.required,
      validationRules: [
        {
          type: 'required',
          message: `${fieldName} alanı zorunludur.`,
          ...(field.required && {})
        },
        {
          type: 'stringLength',
          max: field.maxLength,
          message: `${fieldName} maksimum ${field.maxLength} karakter olabilir.`
        }
      ].filter(rule => Object.keys(rule).length > 1)
    };
  }

  /**
   * Değer validasyonu yap (hata vermeden)
   */
  validateValue(modelName, fieldName, value) {
    const field = this.getFieldValidation(modelName, fieldName);
    
    if (!field || !field.maxLength || typeof value !== 'string') {
      return { valid: true, message: '', truncatedValue: value };
    }

    // Değeri truncate et (hata vermeden)
    const truncatedValue = value.length > field.maxLength 
      ? value.substring(0, field.maxLength) 
      : value;

    return {
      valid: true,
      message: value.length > field.maxLength 
        ? `${fieldName} ${field.maxLength} karakterle sınırlı.` 
        : '',
      truncatedValue
    };
  }

  /**
   * Real-time validasyon için onChange handler
   */
  createValidationHandler(modelName, fieldName, onChange) {
    return (e) => {
      const value = e.event?.target?.value || e.value || '';
      const validation = this.validateValue(modelName, fieldName, value);
      
      // Truncate edilmiş değeri onChange'e gönder
      if (onChange && validation.truncatedValue !== value) {
        // DevExtreme için event'i güncelle
        if (e.event) {
          e.event.target.value = validation.truncatedValue;
        }
        
        // Yeni değeri gönder
        onChange(validation.truncatedValue);
      }
      
      return validation.truncatedValue;
    };
  }

  /**
   * Store'u resetle
   */
  reset() {
    this.modelValidations = {};
    this.uiValidationRules = {};
    this.isLoaded = false;
    this.loading = false;
    this.error = null;
  }

  /**
   * Debug için validasyon bilgilerini log'la
   */
  debug() {
    console.log('🔍 Validasyon Store Debug:');
    console.log('- Loaded:', this.isLoaded);
    console.log('- Loading:', this.loading);
    console.log('- Models:', Object.keys(this.modelValidations));
    console.log('- Error:', this.error);
  }
}

// Singleton instance
const validationStore = new ValidationStore();

export default validationStore;
